// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
import { useId, useState, type ReactNode } from 'react';
import {
  Article,
  Brain,
  BookOpen,
  CalendarCheck,
  Cards,
  ChartBar,
  FileText,
  Lifebuoy,
  Play,
  Plus,
  Question,
  type Icon,
} from '@phosphor-icons/react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { ChatCitation } from '@/types/chatStream';
import { scrollToBlockElement } from './blocks/addedBlockScroll';
import { resolveChatCitationTarget } from './blocks/chatBlockTargets';
import { citationLabel } from './chatSourceLabels';
import { useMcGrawHillSourceLabel } from '../../../../mcgrawHill/mcgrawHillChapter'; // mcgraw-hill

/** Chips shown before the list folds the rest behind "+N more". */
export const MAX_VISIBLE_SOURCES = 8;

/** Circles in the stack next to the "N sources" button. */
export const MAX_STACKED_SOURCES = 3;

/**
 * One icon per citation kind. An unknown kind — a kind this client is older
 * than — still gets a circle rather than a hole in the stack.
 */
const KIND_ICONS: Record<string, Icon> = {
  article: Article,
  question: Question,
  flashcard: Cards,
  video: Play,
  document_page: FileText,
  chat_document_page: FileText,
  help_doc: Lifebuoy,
  performance: ChartBar,
  study_plan: CalendarCheck,
  memory: Brain,
};

const kindIcon = (kind: string): Icon => KIND_ICONS[kind] ?? BookOpen;

// 44px tall where a finger is the pointer, and compact once there is a mouse.
const CHIP_CLASS =
  'inline-flex min-h-11 items-stretch overflow-hidden rounded-lg border border-border-subtle bg-surface-muted text-xs mobile:min-h-7';

const CHIP_BODY_CLASS = 'flex items-center gap-1.5 px-2 py-1 text-left';

const CHIP_ACTION_CLASS =
  'flex items-center border-l border-border-subtle px-1.5 text-fg-secondary transition-colors hover:bg-surface-highlight hover:text-fg-primary disabled:pointer-events-none disabled:opacity-60';

/** The circle the stack and every chip use, so one source reads as one thing. */
const KindIcon = ({ kind, size }: { kind: string; size: number }) => {
  const IconComponent = kindIcon(kind);
  return <IconComponent size={size} aria-hidden="true" />;
};

interface SourceChipProps {
  citation: ChatCitation;
  /** Its item is already a block: the chip says so and offers no plus. */
  isShown: boolean;
  /** The block to scroll back to, when the conversation still holds one. */
  shownBlockId: string | null;
  isPending: boolean;
  onSelect?: (citation: ChatCitation) => void;
  onAdd?: (citation: ChatCitation) => void;
  /** Stagger, so an opened list cascades instead of snapping in. */
  delayMs: number;
}

/**
 * One cited item: its kind, its label, and whatever the student can do with
 * it — open it, add it to the thread, or go back to where it is already
 * shown. A source with nowhere to go is a plain label, never a dead control.
 */
const SourceChip = ({
  citation,
  isShown,
  shownBlockId,
  isPending,
  onSelect,
  onAdd,
  delayMs,
}: SourceChipProps) => {
  const formatSourceLabel = useMcGrawHillSourceLabel(); // mcgraw-hill
  const label = formatSourceLabel(citationLabel(citation));
  const target = isShown ? null : resolveChatCitationTarget(citation);

  const body = (
    <>
      <KindIcon kind={citation.kind} size={12} />
      {/* The title rides on the label itself, so the tooltip is on the words
          whether the chip is a link, a button, or a plain label. */}
      <span className="min-w-0" title={citation.title}>
        {label}
      </span>
      {isShown ? (
        <span className="text-[0.65rem] text-fg-secondary/80">Shown above</span>
      ) : null}
    </>
  );

  return (
    <li
      className={cn(CHIP_CLASS, 'animate-chat-fade-up')}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      {isShown && shownBlockId ? (
        <button
          type="button"
          // The block may have scrolled out of the thread but it is still in
          // the DOM; if it is not there at all the tap simply does nothing.
          onClick={() => scrollToBlockElement(shownBlockId)}
          className={cn(
            CHIP_BODY_CLASS,
            'text-fg-secondary transition-colors hover:bg-surface-highlight hover:text-fg-primary'
          )}
        >
          {body}
        </button>
      ) : target ? (
        <a
          href={target}
          // A surface that can show the source in place (the document viewer's
          // split pane) takes the click; the href is what makes the chip a
          // real link for a new tab, and the fallback with no such surface.
          onClick={(event) => {
            if (!onSelect) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey) return;
            event.preventDefault();
            onSelect(citation);
          }}
          className={cn(
            CHIP_BODY_CLASS,
            'text-fg-primary transition-colors hover:bg-surface-highlight'
          )}
        >
          {body}
        </a>
      ) : (
        <span className={cn(CHIP_BODY_CLASS, 'text-fg-secondary')}>{body}</span>
      )}
      {!isShown && onAdd ? (
        <button
          type="button"
          aria-label={`Add ${label}`}
          aria-busy={isPending || undefined}
          disabled={isPending}
          onClick={() => onAdd(citation)}
          className={CHIP_ACTION_CLASS}
        >
          {isPending ? (
            <Spinner size={12} thickness={3} aria-hidden="true" />
          ) : (
            <Plus size={12} weight="bold" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </li>
  );
};

export interface ChatAnswerFooterProps {
  /** Every citation of the answer, including the ones served as blocks. */
  citations: ChatCitation[];
  /**
   * Block id per citation whose item is already a block in the conversation
   * (`citationBlockIds`) — served by this turn, served by a later one, or
   * added from this footer before a reload.
   */
  shownBlockIds?: ReadonlyMap<string, string>;
  /**
   * Open a source in place. The document viewer's split pane supplies it so a
   * page chip scrolls its own viewer instead of navigating away.
   */
  onSelect?: (citation: ChatCitation) => void;
  /**
   * Add an addable source to the conversation as a block. Resolves true once
   * the block is in the thread; false re-enables the plus. Omitted when the
   * answer cannot take blocks yet (no server id) or is a legacy archive.
   */
  onAdd?: (citation: ChatCitation) => Promise<boolean>;
  /** The answer's action bar (`ChatMessageActions`), on the left. */
  actions?: ReactNode;
  /** Follow-up chips under the footer. Reserved for ORA-606. */
  followups?: ReactNode;
}

/**
 * The footer under a finished answer (ORA-601): what the student can do with
 * the answer on the left, what it was built from on the right.
 *
 * It replaces the two unrelated rows this used to be — a "Sources (N)"
 * disclosure and, below it, the action bar — so the answer ends in one line
 * instead of two. Sources are a stack of kind circles and a count that opens
 * the list, and a source the turn already served as a block is listed too,
 * marked "Shown above" and linked back to its block, rather than dropped
 * (which is what made an answer's evidence disappear entirely when every
 * citation was rendered).
 *
 * `ChatMessageItem` owns when this appears: never while the answer is still
 * streaming, and the row fades in once it is done.
 */
const ChatAnswerFooter = ({
  citations,
  shownBlockIds,
  onSelect,
  onAdd,
  actions,
  followups,
}: ChatAnswerFooterProps) => {
  const listId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [showsAll, setShowsAll] = useState(false);

  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const [addedIds, setAddedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const handleAdd = async (citation: ChatCitation) => {
    if (!onAdd) return;

    setPendingIds((current) => new Set(current).add(citation.id));

    try {
      const added = await onAdd(citation);
      if (added) setAddedIds((current) => new Set(current).add(citation.id));
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(citation.id);
        return next;
      });
    }
  };

  if (citations.length === 0 && !actions && !followups) return null;

  const visible = showsAll
    ? citations
    : citations.slice(0, MAX_VISIBLE_SOURCES);

  const hiddenCount = citations.length - visible.length;
  const stacked = citations.slice(0, MAX_STACKED_SOURCES);

  const sourcesLabel = `${citations.length} ${
    citations.length === 1 ? 'source' : 'sources'
  }`;

  const isShownSource = (citation: ChatCitation) =>
    citation.rendered === true ||
    addedIds.has(citation.id) ||
    (shownBlockIds?.has(citation.id) ?? false);

  return (
    <div
      className="chat-motion animate-chat-fade-in flex flex-col mt-1!"
      data-testid="chat-answer-footer"
    >
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1 mb-3">
        {actions}
        {citations.length > 0 ? (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={listId}
            onClick={() => setIsOpen((open) => !open)}
            className="ml-auto flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-1.5 text-sm text-fg-secondary outline-none transition-colors hover:bg-surface-muted hover:text-fg-primary focus-visible:ring-[3px] focus-visible:ring-ring/50 mobile:min-h-8"
          >
            <span
              className="flex items-center"
              aria-hidden="true"
              data-testid="chat-source-stack"
            >
              {stacked.map((citation, index) => (
                <span
                  key={citation.id}
                  className={cn(
                    'flex size-5 items-center justify-center rounded-full bg-surface-muted text-fg-secondary ring-2 ring-surface-canvas',
                    index > 0 && '-ml-1.5'
                  )}
                >
                  <KindIcon kind={citation.kind} size={11} />
                </span>
              ))}
            </span>
            {sourcesLabel}
          </button>
        ) : null}
      </div>

      {citations.length > 0 ? (
        <div
          id={listId}
          className="grid transition-[grid-template-rows,opacity] duration-300 ease-chat-out"
          style={{
            gridTemplateRows: isOpen ? '1fr' : '0fr',
            opacity: isOpen ? 1 : 0,
          }}
        >
          <div className="overflow-hidden">
            {isOpen ? (
              <ul
                className="m-0 mt-1.5 flex list-none flex-wrap items-center gap-1.5 p-0"
                aria-label="Sources"
                data-testid="chat-sources"
              >
                {visible.map((citation, index) => {
                  const isShown = isShownSource(citation);

                  return (
                    <SourceChip
                      key={citation.id}
                      citation={citation}
                      isShown={isShown}
                      shownBlockId={shownBlockIds?.get(citation.id) ?? null}
                      isPending={pendingIds.has(citation.id)}
                      onSelect={onSelect}
                      onAdd={
                        onAdd && citation.addable === true && !isShown
                          ? (target) => void handleAdd(target)
                          : undefined
                      }
                      delayMs={Math.min(index, 5) * 60}
                    />
                  );
                })}
                {hiddenCount > 0 ? (
                  <li>
                    <button
                      type="button"
                      onClick={() => setShowsAll(true)}
                      className="flex min-h-11 items-center rounded-lg border border-dashed border-border-subtle px-2 text-xs text-fg-secondary transition-colors hover:text-fg-primary mobile:min-h-7"
                    >
                      +{hiddenCount} more
                    </button>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      {followups}
    </div>
  );
};

export default ChatAnswerFooter;
